import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatBadgeModule } from "@angular/material/badge";
import { MatDividerModule } from "@angular/material/divider";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { Subscription } from "rxjs";
import { ToastService } from "../../services/toast.service";
import { MatCardModule } from "@angular/material/card";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AppNotification } from "./app-notification.model";
import { NotificationHostComponent } from "./notification-host/notification-host.component";
import { getNotificationRegistryEntry } from "./notification-registry";
import { WebSocketNotificationOrchestratorService } from "../../services/websocket-notification-orchestrator.service";

@Component({
  selector: "app-notifications",
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatBadgeModule,
    MatDividerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatCardModule,
    TranslateModule,
    NotificationHostComponent,
  ],
  templateUrl: "./notifications.component.html",
  styleUrls: ["./notifications.component.scss"],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private readonly orchestrator = inject(WebSocketNotificationOrchestratorService);
  private readonly toastr = inject(ToastService);
  private readonly translateSvc = inject(TranslateService);
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild("menuBody") menuBody?: ElementRef<HTMLElement>;

  isConnected = false;
  showDevWebSocketToggle = false;
  devWebSocketEnabled = true;
  notifications: AppNotification[] = [];
  totalUnreadCount = 0;

  private connectionSub?: Subscription;
  private notificationsSub?: Subscription;
  private notificationAddedSub?: Subscription;
  private notificationSound: HTMLAudioElement | undefined;
  private visibilityObserver?: IntersectionObserver;
  private readonly seenWhileMenuOpen = new Set<string>();
  private menuIsOpen = false;

  async ngOnInit(): Promise<void> {
    this.initNotificationSound();
    this.showDevWebSocketToggle =
      this.orchestrator.isDevWebSocketToggleAvailable();
    this.devWebSocketEnabled =
      this.orchestrator.isDevWebSocketConnectionEnabled();
    await this.orchestrator.initialize();

    this.connectionSub = this.orchestrator
      .getConnectionStatus()
      .subscribe((connected) => {
        this.isConnected = connected;
      });

    this.notificationsSub = this.orchestrator.notifications$.subscribe(
      (notifications) => {
        this.notifications = notifications;
        this.updateUnreadCount();

        if (this.menuIsOpen) {
          this.scheduleVisibilityObserverSetup();
        }
      },
    );

    this.notificationAddedSub = this.orchestrator.notificationAdded$.subscribe(
      (notification) => {
        this.playNotificationSound();
        this.showToastNotification(notification);
      },
    );
  }

  ngOnDestroy(): void {
    this.teardownVisibilityObserver();
    this.connectionSub?.unsubscribe();
    this.notificationsSub?.unsubscribe();
    this.notificationAddedSub?.unsubscribe();
  }

  private updateUnreadCount(): void {
    this.totalUnreadCount = this.notifications.filter((n) => n.isNew).length;
  }

  onMenuOpened(): void {
    this.menuIsOpen = true;
    this.seenWhileMenuOpen.clear();
    this.scheduleVisibilityObserverSetup();
  }

  onMenuClosed(): void {
    this.menuIsOpen = false;
    this.teardownVisibilityObserver();

    if (this.seenWhileMenuOpen.size > 0) {
      this.orchestrator.markNotificationsAsSeen([...this.seenWhileMenuOpen]);
    }

    this.seenWhileMenuOpen.clear();
    this.updateUnreadCount();
  }

  activateNotification(notificationId: string): void {
    this.menuTrigger.closeMenu();
    this.orchestrator.removeNotification(notificationId);
  }

  deleteNotification(notificationId: string): void {
    this.orchestrator.removeNotification(notificationId);
  }

  async reconnect(): Promise<void> {
    await this.orchestrator.reconnect();
  }

  async onDevWebSocketToggle(enabled: boolean): Promise<void> {
    this.devWebSocketEnabled = enabled;
    await this.orchestrator.setDevWebSocketConnectionEnabled(enabled);
  }

  private scheduleVisibilityObserverSetup(): void {
    setTimeout(() => this.setupVisibilityObserver(), 0);
  }

  private setupVisibilityObserver(): void {
    const root = this.menuBody?.nativeElement;
    if (!root || !this.menuIsOpen) {
      return;
    }

    this.teardownVisibilityObserver();

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }

          const notificationId = (entry.target as HTMLElement).dataset[
            "notificationId"
          ];
          if (notificationId) {
            this.seenWhileMenuOpen.add(notificationId);
          }
        }
      },
      {
        root,
        threshold: 0.25,
      },
    );

    root
      .querySelectorAll<HTMLElement>("[data-notification-id]")
      .forEach((element) => {
        this.visibilityObserver?.observe(element);
      });
  }

  private teardownVisibilityObserver(): void {
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = undefined;
  }

  private showToastNotification(notification: AppNotification): void {
    const entry = getNotificationRegistryEntry(notification.eventType);
    if (!entry) {
      return;
    }

    if (entry.getToastMessage) {
      this.toastr.success(entry.getToastMessage(notification.payload));
      return;
    }

    if (entry.toastKey) {
      this.toastr.success(
        this.translateSvc.instant(`notification.${entry.toastKey}`),
      );
    }
  }

  private initNotificationSound(): void {
    try {
      this.notificationSound = new Audio(
        "/assets/sounds/notification-sound-effect-372475.mp3",
      );
      this.notificationSound.volume = 0.5;
      this.notificationSound.preload = "auto";

      this.notificationSound.onerror = () => {
        console.warn("Notification sound file not found, using fallback beep");
        this.createSimpleBeep();
      };
    } catch (error) {
      console.warn("Could not load notification sound:", error);
      this.createSimpleBeep();
    }
  }

  private createSimpleBeep(): void {
    try {
      const audioContext = new (window.AudioContext ||
        (window as Window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext!)();

      this.notificationSound = {
        play: () => {
          return new Promise<void>((resolve, reject) => {
            try {
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();

              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);

              oscillator.frequency.value = 800;
              gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
              gainNode.gain.exponentialRampToValueAtTime(
                0.01,
                audioContext.currentTime + 0.3,
              );

              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.3);
              oscillator.onended = () => resolve();
              setTimeout(() => resolve(), 300);
            } catch (error) {
              reject(error);
            }
          });
        },
        currentTime: 0,
      } as HTMLAudioElement;
    } catch (error) {
      console.warn("Could not create fallback beep:", error);
    }
  }

  private playNotificationSound(): void {
    if (!this.notificationSound) {
      return;
    }

    try {
      this.notificationSound.currentTime = 0;
      this.notificationSound.play().catch((error) => {
        console.warn("Could not play notification sound:", error);
      });
    } catch (error) {
      console.warn("Error playing notification sound:", error);
    }
  }
}
